// =============================================================
// Register Page
// =============================================================

import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ApiError } from '../lib/api';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    organizationName: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  function update(field: keyof typeof form) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      await register(form);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-bg-gradient" />

      <div className="auth-left">
        <div className="auth-brand">
          <div className="auth-brand-icon"></div>
          <span className="auth-brand-name">Local SEO AI</span>
        </div>

        <div className="auth-tagline">
          <h1>
            Your local SEO <span>command center</span>
          </h1>
          <p>
            Set up your organization in minutes. Connect your businesses,
            start tracking local rankings, and get AI-powered recommendations.
          </p>
        </div>

        <div className="auth-features">
          {[
            { icon: '', text: 'Multi-location business management' },
            { icon: '', text: 'Real-time local ranking data' },
            { icon: '', text: 'AI content & response generation' },
            { icon: '', text: 'Approval workflow before publishing' },
          ].map((f) => (
            <div className="auth-feature" key={f.text}>
              <div className="auth-feature-icon">{f.icon}</div>
              <span className="auth-feature-text">{f.text}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="auth-right">
        <div className="auth-form-container">
          <h2 className="auth-form-title">Create your account</h2>
          <p className="auth-form-subtitle">Get started  it's free to set up</p>

          {error && (
            <div className="alert alert-error" role="alert">
              <span></span> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName" className="form-label required">
                  First Name
                </label>
                <input
                  id="firstName"
                  type="text"
                  className="form-input"
                  placeholder="Jane"
                  value={form.firstName}
                  onChange={update('firstName')}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName" className="form-label required">
                  Last Name
                </label>
                <input
                  id="lastName"
                  type="text"
                  className="form-input"
                  placeholder="Smith"
                  value={form.lastName}
                  onChange={update('lastName')}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="reg-email" className="form-label required">
                Work Email
              </label>
              <input
                id="reg-email"
                type="email"
                className="form-input"
                placeholder="jane@company.com"
                value={form.email}
                onChange={update('email')}
                required
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="organizationName" className="form-label required">
                Organization Name
              </label>
              <input
                id="organizationName"
                type="text"
                className="form-input"
                placeholder="My Agency or Business Name"
                value={form.organizationName}
                onChange={update('organizationName')}
                required
              />
              <span className="form-hint">
                This becomes your workspace. You can manage multiple businesses inside it.
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="reg-password" className="form-label required">
                Password
              </label>
              <input
                id="reg-password"
                type="password"
                className="form-input"
                placeholder="Min. 8 characters"
                value={form.password}
                onChange={update('password')}
                required
                autoComplete="new-password"
                minLength={8}
              />
            </div>

            <button
              id="register-submit-btn"
              type="submit"
              className={`btn btn-primary btn-full btn-lg ${loading ? 'btn-loading' : ''}`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="loading-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div className="auth-form-footer">
            Already have an account?{' '}
            <Link to="/login">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
